import apiClient, { addJsonHeader, removeJsonHeader } from 'models/apiClient';
import { authApi, artistsApi, formsApi } from 'api/client/clients';
import { LoginRequest, LoginResponse } from 'models/LoginRequest';
import { ApiResponse } from 'models/ApiResponse';
import { UserSignUp } from "models/UserSignUp";
import { FormDto } from 'models/forms/FormDto';
import { Form } from 'models/forms/Form';
import { WorkflowDto } from 'models/workflows/WorkflowDto';
import { Workflow } from 'models/workflows/Workflow';
import { Artist } from 'models/Artist';
import { ArtistImage } from 'models/ArtistImage';
import { ArtistHeroSection } from 'interfaces/ArtistHeroSection';
import { ArtistSignup } from 'models/ArtistSignup';
import { ArtistDto } from 'models/ArtistDto';
import { LocationSearchRequest } from 'models/locations/LocationSearchRequest';
import { LocationDto } from 'models/locations/LocationDto';
import { ArtistLocationDto } from 'interfaces/ArtistLocationDto';
import { ArtistLocationsSearchRequest } from 'models/ArtistLocationsSearchRequest';
import { Team } from 'models/teams/Team';
import { TeamInvite } from 'models/teams/TeamInvite';
import { TeamReviewSettings } from 'models/teams/teamReview/TeamReviewSettings';
import { toTeamReviewSettingsDto } from 'models/teams/teamReview/TeamReviewSettingsDto';
import { Project } from 'models/projects/Project';
import { ProjectSearchParams } from 'models/projects/ProjectSearchParams';
import { FormSubmissionDto } from 'models/projects/FormSubmission';
import { UserSubmission } from 'models/projects/UserSubmission';
import { MemberVote } from 'models/projects/MemberVote';
import type { SessionUser } from 'models/auth/SessionUser';
import { utilities } from 'utils/utilities';
import { setAccessToken } from 'models/auth/accessTokenStore';
import { wrapAsApiResponse } from 'models/tattoos/tattooApiHelpers';
import { TattooCategory, CreateTattooCategoryRequest, UpdateTattooCategoryRequest } from 'models/tattoos/TattooCategory';
import { TattooStyle, CreateTattooStyleRequest, UpdateTattooStyleRequest } from 'models/tattoos/TattooStyle';
import {
  ArtistTattooStyleAssignment,
  AddArtistTattooStyleRequest,
  UpdateArtistTattooStyleAssignmentRequest,
} from 'models/tattoos/ArtistTattooStyle';
import ApiEndpoints from 'services/UrlGenerator';

export class ApiService {

  static async getMe(): Promise<SessionUser> {
    // Deterministic: user can be at most one artist
    try {
      const session = await apiClient.get<ApiResponse<SessionUser>>(ApiEndpoints.sessions);
      console.log("getMe session response: ", session);
      if (session && session.data.success && session.data.data) {
        const sessionUser = session.data.data;
        return {
          userId: sessionUser.userId,
          isArtist: sessionUser.isArtist,
          artistId: sessionUser.artistId,
          teams: sessionUser.teams,
        };
      }
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        throw e;
      }

      // Not an artist (or API returned non-auth error)
    }

    const id = localStorage.getItem('userId') || utilities.getUserId();
    if (!id) {
      throw new Error('No session available');
    }

    // Non-artist teamIds[] not currently exposed; return empty for now.
    return {
      userId: id,
      isArtist: false,
      teams: [],
    };
  }

  static async loginUser(loginRequest: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    // Use generated client with shared interceptors
    const response: any = await authApi.apiAuthenticationLoginPost({ loginRequest });

    if (response.data?.success && response.data?.data) {
      setAccessToken(response.data.data.token);
      localStorage.setItem("userId", response.data.data.userId);
    }

    return response.data;
  }

  static async signUpUser(signupRequest: UserSignUp): Promise<ApiResponse<any>> {
    const response = await apiClient.post<ApiResponse<any>>(ApiEndpoints.signUpUser, signupRequest);

    console.log(response);
    if (response.data.success === true && response.data.data) {
      setAccessToken(response.data.data.token);
      localStorage.setItem("userId", response.data.data.userId);
    }

    return response.data;
  }

  static async signUpArtist(signupRequest: ArtistSignup): Promise<ApiResponse<any>> {
    const response = await apiClient.post<ApiResponse<any>>(ApiEndpoints.signUpArtist, signupRequest);

    console.log(response);
    if (response.data.success === true && response.data.data) {
      setAccessToken(response.data.data.token);
      localStorage.setItem("userId", response.data.data.artist.userId);
    }

    return response.data;
  }

  // Forms
  static async getFormSchema(teamId: string): Promise<ApiResponse<any>> {
    try {
      // Make an API call to fetch the form schema
      const response = await apiClient.get<ApiResponse<any>>(ApiEndpoints.getFormSchema(teamId));

      if (response.data.success === true && response.data.data) {
        //console.log('Form schema fetched successfully:', response.data.data);
        return response.data.data; // Return the Form object
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        return response.data.data.errors;
      }
    } catch (error: any) {
      console.error('Network or unexpected error occurred:', error);
      return error;
    }
  };

  static async getFormElementsList(teamId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(ApiEndpoints.getFormElements(teamId));

      if (response.data.success === true && response.data.data) {
        console.log("Form element list returned successfully: ", response.data.data);
        return response.data.data;
      }
      else {
        // Handle errors in the API response
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        return response.data.data.errors;
      }
    }
    catch (error: any) {
      // Handle network or unexpected errors
      console.error('Network or unexpected error occurred:', error);
      return error;
    }
  }

  static async getForms(teamId: string, getDtos: boolean): Promise<ApiResponse<FormDto[] | Form[]>> {
    try {
      const response = await apiClient.get<ApiResponse<FormDto[] | Form[]>>(ApiEndpoints.getForms(teamId), {
        params: { getDtos }, // Pass the parameter to the API
      });

      if (response.data.success && response.data.data) {
        console.log('Forms fetched successfully:', response.data.data);
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching forms');
      }
    } catch (error: any) {
      console.error('Network or unexpected error occurred:', error);
      throw error;
    }
  }

  static async getForm(teamId: string, formId: string): Promise<ApiResponse<Form>> {
    try {
      const response = await apiClient.get<ApiResponse<Form>>(ApiEndpoints.getForm(teamId, formId), {
        params: { objectId: formId },
      });

      if (response.data.success && response.data.data) {
        console.log('Forms fetched successfully:', response.data.data);
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching forms');
      }
    } catch (error: any) {
      console.error('Network or unexpected error occurred:', error);
      throw error;
    }
  }

  static async saveForm(teamId: string, form: Form): Promise<ApiResponse<any>> {
    try {

      const payload = JSON.stringify(form);
      const response = await apiClient.post<ApiResponse<Form>>(ApiEndpoints.saveForm(teamId), payload);

      if (response.data.success && response.data.data) {
        console.log('Form saved successfully:', response.data.data);
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error saving form');
      }
    } catch (error: any) {
      if (error.response) {
        console.error('Server response error:', error.response.data);
      } else {
        console.error('Unexpected error occurred:', error.message);
      }
      throw error;
    }
  }

  static async updateForm(teamId: string, form: Form): Promise<ApiResponse<any>> {
    try {
      if (!form?.id || form.id === utilities.DEFAULT_GUID) {
        throw new Error('updateForm requires an existing form id');
      }

      const payload = JSON.stringify(form);
      const response = await apiClient.put<ApiResponse<Form>>(ApiEndpoints.updateForm(teamId, form.id), payload, {
        params: { objectId: form.id },
      });

      if (response.data.success && response.data.data) {
        console.log('Form updated successfully:', response.data.data);
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error updating form');
      }
    } catch (error: any) {
      if (error.response) {
        console.error('Server response error:', error.response.data);
      } else {
        console.error('Unexpected error occurred:', error.message);
      }
      throw error;
    }
  }

  static async deleteForm(teamId: string, formId: string): Promise<ApiResponse<any>> {

    try {
      const response = await apiClient.delete<ApiResponse<any>>(ApiEndpoints.deleteForm(teamId, formId),
        {
          params: { objectId: formId },
        });

      if (response.data.success && response.data.data) {
        console.log('Forms fetched successfully:', response.data.data);
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching forms');
      }
    } catch (error: any) {
      if (error.response) {
        console.error('Server response error:', error.response.data);
      } else {
        console.error('Unexpected error occurred:', error.message);
      }
      throw error;
    }
  }



  static async getWorkflows(teamId: string): Promise<ApiResponse<WorkflowDto[]>> {

    try {
      const response = await apiClient.get<ApiResponse<WorkflowDto[]>>(ApiEndpoints.getWorkflows(teamId));

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching workflows');
      }
    }
    catch (error: any) {
      console.error('Network or unexpected error occurred:', error);
      throw error;
    }
  }

  static async getWorkflow(teamId: string, workflowId: string): Promise<ApiResponse<Workflow>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getWorkflow(teamId, workflowId),
        {
          params: { objectId: workflowId },
        });

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching workflows');
      }
    } catch (error) {
      console.error(`Failed to retrieve workflow with ID: ${workflowId}`, error);
      throw error;
    }
  }

  static async insertWorkflow(teamId: string, workflow: Workflow): Promise<ApiResponse<any>> {
    try {
      const dto = workflow.toDto();
      const response = await apiClient.post(ApiEndpoints.insertWorkflow(teamId), workflow);

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching workflows');
      }
    } catch (error) {
      console.error(`Failed to save new workflow!`, error);
      throw error;
    }
  }

  static async updateWorkflow(teamId: string, workflow: Workflow): Promise<ApiResponse<any>> {
    try {
      const dto = workflow.toDto();

      const params = { objectId: workflow.id };

      const response = await apiClient.put(ApiEndpoints.updateWorkflow(teamId), dto, { params });

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching workflows');
      }
    } catch (error) {
      console.error(`Failed to save new workflow!`, error);
      throw error;
    }
  }

  static async deleteWorkflow(teamId: string, workflowId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.delete(ApiEndpoints.deleteWorkflow(teamId, workflowId),
        {
          params: { objectId: workflowId },
        });

      if (response.data.success) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error deleting workflowid: ' + workflowId);
      }
    } catch (error) {
      console.error(`Failed to delete  workflow!`, error);
      throw error;
    }
  }

  static async getArtistInfo(artistId: string): Promise<ApiResponse<Artist>> {

    try {
      const response: any = await artistsApi.apiArtistsIdGet({ id: artistId });

      if (response.data.success && response.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error finding artist: ' + artistId);
      }
    } catch (error) {
      console.error(`Failed to fetch artist!`, error);
      throw error;
    }
  }

  static async uploadArtistImage(artistId: string, file: FormData): Promise<ApiResponse<ArtistImage>> {

    try {
      removeJsonHeader();
      const response = await apiClient.post(ApiEndpoints.uploadArtistImage(artistId), file);

      if (response.data.success && response.data) {
        //console.log("in api service " + response.data)
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error uploading picture: ');
      }
    } catch (error) {
      console.error(`Failed to upload picture!`, error);
      throw error;
    }
    finally {
      addJsonHeader();
    }
  }

  static async updateArtistImage(artistId: string, artistImageId: string, imageTitle: string): Promise<ApiResponse<ArtistImage>> {
    try {
      // Ensure the payload matches the server's expectations
      const payload = { artistImageTitle: imageTitle };
      const response = await apiClient.put(ApiEndpoints.updateArtistImage(artistId, artistImageId), payload,
        {
          params: { objectId: artistImageId },
        }
      );

      if (response.data.success && response.data) {
        return response.data;
      } else {
        console.error('Validation Errors:', response.data.errors);
        throw new Error('Error updating artist image title');
      }
    } catch (error) {
      console.error(`Failed to update artist image title!`, error);
      throw error;
    }
  }

  static async deleteArtistImage(artistId: string, artistImageId: string): Promise<ApiResponse<any>> {

    try {
      const response = await apiClient.delete(ApiEndpoints.deleteArtistImage(artistId, artistImageId),
        {
          params: { objectId: artistImageId },
        });

      if (response.data.success && response.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error deleting artist image: ' + artistImageId);
      }
    } catch (error) {
      console.error(`Failed to delete artist image!`, error);
      throw error;
    }
  }

  static async getArtistImages(artistId: string): Promise<ApiResponse<ArtistImage[]>> {

    try {
      const response = await apiClient.get(ApiEndpoints.getArtistImages(artistId));

      if (response.data.success && response.data) {
        //console.log("in api service " + response.data)
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error uploading picture: ');
      }
    } catch (error) {
      console.error(`Failed to upload picture!`, error);
      throw error;
    }
  }

  static async getArtistHeroSection(artistId: string): Promise<ApiResponse<ArtistHeroSection>> {

    try {
      const response = await apiClient.get(ApiEndpoints.getArtistHeroSection(artistId));
      if (response.data.success && response.data) {
        return response.data;
      }
      else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error getting hero section: ');
      }
    }
    catch (error) {
      console.error(`Failed to retrieve hero section!`, error);
      throw error;
    }
  }

  static async saveArtistHeroSectionAsync(
    artistId: string,
    section: { headline: string; subheadline: string; backgroundImageUrlId: string; bookingLinks?: WorkflowDto[] }
  ): Promise<ApiResponse<ArtistHeroSection>> {

    try {
      let sectionDto = {
        artistId: artistId.toUpperCase(),
        headline: section.headline || "",
        subheadline: section.subheadline || "",
        backgroundImageUrlId: section.backgroundImageUrlId || "",
        bookingLinks: section.bookingLinks,
      };

      const response = await apiClient.post(ApiEndpoints.saveArtistHeroSection(artistId), sectionDto);

      if (response.data.success && response.data) {
        return response.data;
      }
      else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error getting hero section: ');
      }
    }
    catch (error) {
      console.error(`Failed to retrieve hero section!`, error);
      throw error;
    }
  }

  static async updateArtistHeroSectionAsync(
    artistId: string,
    section: { headline: string; subheadline: string; backgroundImageUrlId: string; bookingLinks?: WorkflowDto[] }
  ): Promise<ApiResponse<ArtistHeroSection>> {

    try {
      let sectionDto = {
        artistId: artistId.toUpperCase(),
        headline: section.headline || "",
        subheadline: section.subheadline || "",
        backgroundImageUrlId: section.backgroundImageUrlId || "",
        bookingLinks: section.bookingLinks,
      };

      const response = await apiClient.put(ApiEndpoints.updateArtistHeroSection(artistId), sectionDto,
        {
          params: { objectId: artistId },
        });

      if (response.data.success && response.data) {
        return response.data;
      }
      else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error getting hero section: ');
      }
    }
    catch (error) {
      console.error(`Failed to retrieve hero section!`, error);
      throw error;
    }
  }

  static async checkAdminAccess(): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get<ApiResponse<any>>(ApiEndpoints.checkAdminAccess);
      return response.data;
    } catch (error) {
      console.error('Error checking admin access:', error);
      throw error;
    }
  }

  static async createSimpleArtist(artistDto: ArtistDto): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(ApiEndpoints.createSimpleArtist, artistDto);
      return response.data;
    } catch (error) {
      console.error('Error creating simple artist:', error);
      throw error;
    }
  }

  static async searchLocations(searchRequest: LocationSearchRequest): Promise<ApiResponse<LocationDto[]>> {
    try {
      console.log("Query String:", new URLSearchParams(searchRequest as any).toString());
      const response = await apiClient.get<ApiResponse<LocationDto[]>>(
        ApiEndpoints.searchLocations,
        {
          params: searchRequest
        }
      );
      console.log("response in searchLocations: ", response.data);
      return response.data;
    } catch (error) {
      console.error("Error searching locations:", error);
      throw error;
    }
  }

  static async getLocation(locationId: string): Promise<ApiResponse<LocationDto>> {
    try {
      const response = await apiClient.get<ApiResponse<LocationDto>>(ApiEndpoints.getLocation(locationId));
      return response.data;
    } catch (error) {
      console.error('Error fetching location:', error);
      throw error;
    }
  }

  static async getArtistLocations(artistId: string): Promise<ApiResponse<ArtistLocationDto[]>> {

    const searchRequest = new ArtistLocationsSearchRequest({
      artistId: artistId
    });

    try {
      const response = await apiClient.get<ApiResponse<ArtistLocationDto[]>>(ApiEndpoints.getArtistLocations,
        {
          params: searchRequest
        });
      return response.data;
    } catch (error) {
      console.error('Error fetching artist locations:', error);
      throw error;
    }
  }

  static async getArtistLocationsBySearchRequest(search: ArtistLocationsSearchRequest): Promise<ApiResponse<ArtistDto[]>> {

    try {
      const response = await apiClient.get<ApiResponse<ArtistDto[]>>(ApiEndpoints.getArtistLocations,
        {
          params: search
        });

      return response.data;
    } catch (error) {
      console.error('Error fetching artist locations:', error);
      throw error;
    }
  }

  static async saveArtistLocation(artistLocation: ArtistLocationDto): Promise<ApiResponse<ArtistLocationDto>> {
    try {
      const response = await apiClient.post<ApiResponse<ArtistLocationDto>>(ApiEndpoints.saveArtistLocation, artistLocation);
      return response.data;
    } catch (error) {
      console.error('Error saving artist location:', error);
      throw error;
    }
  }

  static async updateArtistLocation(artistLocation: ArtistLocationDto): Promise<ApiResponse<ArtistLocationDto>> {
    try {
      const response = await apiClient.put<ApiResponse<ArtistLocationDto>>(ApiEndpoints.updateArtistLocation, artistLocation,
        {
          params: { objectId: artistLocation.artistLocationId },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating artist location:', error);
      throw error;
    }
  }

  static async deleteArtistLocation(artistLocationId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(ApiEndpoints.deleteArtistLocation(artistLocationId),
        {
          params: { objectId: artistLocationId },
        });
      return response.data;
    } catch (error) {
      console.error('Error deleting artist location:', error);
      throw error;
    }
  }

  static async searchArtistsByNameOrUsername(
    params: { query: string },
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<ArtistDto[]>> {
    try {
      const response = await apiClient.get<ApiResponse<ArtistDto[]>>(ApiEndpoints.searchArtists, {
        params,
        signal: options?.signal,
      });

      console.log("response in searchArtistsByNameOrUsername: ", response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching artist locations:', error);
      throw error;
    }
  }

  static async getTeamAsync(teamId: string, artistId: string = ''): Promise<ApiResponse<Team>> {
    try {
      const objectId = teamId.length > 0 ? teamId : artistId;
      const params: any = {};

      if (artistId.length > 0) params.artistId = artistId;
      if (teamId) params.teamId = teamId;
      
      params.objectId = objectId;

      const response = await apiClient.get<ApiResponse<any>>(ApiEndpoints.getTeam, {
        params
      });

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching team: ' + teamId);
      }
    } catch (error) {
      console.error(`Failed to fetch team!`, error);
      throw error;
    }
  }

  static async getUserTeams(userId: string): Promise<ApiResponse<Team[]>> {
     try {
       const params: any = {};
      params.objectId = userId;

      const response = await apiClient.get<ApiResponse<Team[]>>(ApiEndpoints.getTeam,
        {
          params
        }
      );

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching teams');
      }
    } catch (error) {
      console.error(`Failed to fetch team!`, error);
      throw error;
    }
  }

  static async getTeamInvites(teamId: string, userId: string): Promise<TeamInvite[]> {
    try {
      const params: any = {};
      if (userId) params.userId = userId;
      params.objectId = teamId;
      const response = await apiClient.get<ApiResponse<any>>(ApiEndpoints.getTeamInvites(teamId), {
        params
      });

      if (response.data.success && response.data.data) {
        const mapped = response.data.data.map((invite: any) => {
          const obj = Object.assign(new TeamInvite(), invite);
          // Optionally convert dateInviteSent to Date object
          obj.dateInviteSent = new Date(invite.dateInviteSent);
          // Optionally convert status to string for display
          obj.statusString = TeamInvite.getStatusString(invite.status);
          return obj;
        });
        return mapped as TeamInvite[];
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching team invites: ' + teamId);
      }
    } catch (error) {
      console.error(`Failed to fetch team invites!`, error);
      throw error;
    }
  }

  static async createTeamInvite(teamId: string, email: string): Promise<ApiResponse<TeamInvite>> {
    try {
      const params: any = {};
      console.log("Creating team invite for teamId:", teamId, "with email:", email);
      params.objectId = teamId;
      const response = await apiClient.post<ApiResponse<TeamInvite>>(
        ApiEndpoints.createTeamInvite(teamId),
        email, // send email as a JSON string in the request body
        {
          params
        }
      );

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error creating team invite: ' + teamId);
      }
    } catch (error) {
      console.error(`Failed to create team invite!`, error);
      throw error;
    }
  }

  static async RemoveTeamMember(teamId: string, userId: string): Promise<ApiResponse<any>> {
    try {

      const params: any = {};
      params.objectId = teamId;
      params.userId = userId;
      const response = await apiClient.delete<ApiResponse<any>>(ApiEndpoints.deleteTeamInvite(teamId, userId),
        {
          params
        });

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error removing team member: ' + userId);
      }
    } catch (error) {
      console.error(`Failed to remove team member!`, error);
      throw error;
    }
  }

  static async acceptTeamInvite(teamId: string, email: string, token: string): Promise<ApiResponse<any>> {
    try {

      const response = await apiClient.get<ApiResponse<any>>(ApiEndpoints.acceptTeamInvite(teamId), {
        params: {
          email,
          token
        }
      });

      //console.log("Accept team invite response:", response.data);

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error accepting team invite: ' + teamId);
      }
    } catch (error) {
      console.error(`Failed to accept team invite!`, error);
      throw error;
    }
  }

  static async createTeamReviewSettings(teamReviewSettings: TeamReviewSettings): Promise<ApiResponse<TeamReviewSettings>> {
    try {
      const dto = toTeamReviewSettingsDto(teamReviewSettings);
      console.log(dto);
      const response = await apiClient.post<ApiResponse<TeamReviewSettings>>(ApiEndpoints.createTeamReviewSettings(teamReviewSettings.teamId), dto);

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error creating team review settings: ' + teamReviewSettings.teamId);
      }
    } catch (error) {
      console.error(`Failed to create team review settings!`, error);
      throw error;
    }
  }

  static async getTeamReviewSettings(teamId: string, teamReviewSettingsId: string): Promise<ApiResponse<TeamReviewSettings>> {
    try {
      const params: any = {};
      params.objectId = teamId;
      if (teamReviewSettingsId && teamReviewSettingsId.length > 0) {
        params.teamReviewSettingsId = teamReviewSettingsId;
      }
      const response = await apiClient.get<ApiResponse<TeamReviewSettings>>(ApiEndpoints.getTeamReviewSettings(teamId));

      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching team review settings: ' + teamId);
      }
    } catch (error) {
      console.error(`Failed to fetch team review settings!`, error);
      throw error;
    }
  }

  static async updateTeamReviewSettings(teamReviewSettings: TeamReviewSettings): Promise<any> {
    try {
      const response = await apiClient.put<ApiResponse<TeamReviewSettings>>(ApiEndpoints.updateTeamReviewSettings(teamReviewSettings.teamId), teamReviewSettings,
        {
          params: { objectId: teamReviewSettings.teamReviewSettingsId },
        }
      );

      return response.status;

    } catch (error) {
      console.error(`Failed to update team review settings!`, error);
      throw error;
    }
  }


  static async getProjects(artistId: string, teamId: string): Promise<ApiResponse<Project[]>> {
    try {
      const params: any = {};
      if (teamId && teamId.length > 0) {
        params.teamId = teamId;
      } 

      const response = await apiClient.get<ApiResponse<Project[]>>(ApiEndpoints.getProjects, { params });
      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching projects: ' + artistId);
      }
    } catch (error) {
      console.error(`Failed to fetch projects!`, error);
      throw error;
    }
  }

  static async getProjectsBySearch(searchModel: ProjectSearchParams): Promise<ApiResponse<Project[]>> {
    try {
      const params: any = {};
      if (searchModel.teamId && searchModel.teamId.length > 0) {
        params.teamId = searchModel.teamId;
      }
      if (searchModel.workflowId && searchModel.workflowId.length > 0) {
        params.workflowId = searchModel.workflowId;
      }
      if (searchModel.projectId && searchModel.projectId.length > 0) {
        params.projectId = searchModel.projectId;
      }
      if (searchModel.createPartialProject) {
        params.createPartialProject = searchModel.createPartialProject;
      }

      const response = await apiClient.get<ApiResponse<Project[]>>(ApiEndpoints.getProjects, { params });
      if (response.data.success && response.data.data) {
        return response.data;;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching projects: ' + JSON.stringify(searchModel));
      }
    } catch (error) {
      console.error(`Failed to fetch projects!`, error);
      throw error;
    }
  }

  static async getProject(projectId: string): Promise<ApiResponse<Project>> {
    try {
      const params: any = {};
      params.objectId = projectId;

      const response = await apiClient.get<ApiResponse<Project>>(ApiEndpoints.getProject(projectId), { params });
      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching project: ' + projectId);
      }
    } catch (error) {
      console.error(`Failed to fetch project!`, error);
      throw error;
    }
  }

  static async getProjectsWorkflow(workflowId: string): Promise<ApiResponse<Workflow>> {
    try {
      const params: any = {};
      params.objectId = workflowId;
      params.workflowId = workflowId;
      const response = await apiClient.get<ApiResponse<Workflow>>(ApiEndpoints.getProjects, { params });
      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error fetching workflow: ' + workflowId);
      }
    } catch (error) {
      console.error(`Failed to fetch workflow!`, error);
      throw error;
    }
  }

  static async updateProjectNameAndDescription(projectId: string, projectName: string, projectDescription: string): Promise<ApiResponse<Project>> {
    try {
        const response = await apiClient.put<ApiResponse<Project>>(ApiEndpoints.updateProject(projectId), {
          Name: projectName,
          Description: projectDescription
        });
        if (response.data.success && response.data.data) {
          return response.data;
        } else {
          console.error('API Error:', response.data.errors || 'Unknown error occurred');
          throw new Error('Error updating project: ' + projectId);
        }
    } catch (error){
      console.error(`Failed to update project!`, error);
      throw error;
    }
  }
  static async submitFormSubmission(formSubmissionDto: FormSubmissionDto): Promise<ApiResponse<UserSubmission>>{
    try {
      const response = await apiClient.post<ApiResponse<UserSubmission>>(ApiEndpoints.submitFormSubmission, formSubmissionDto);
      if (response.data.success && response.data.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error submitting form: ' + JSON.stringify(formSubmissionDto));
      }
    } catch (error) {
      console.error(`Failed to submit form!`, error);
      throw error;
    }
  }

  static async submitTeamMemberReviewVote(memberVote: MemberVote): Promise<any> {
    const projectId = memberVote.projectId;
    const teamReviewId = memberVote.teamReviewId;
    try {
      const response = await apiClient.put<ApiResponse<any>>(ApiEndpoints.submitTeamMemberReviewVote(projectId, teamReviewId), memberVote);
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(`Failed to submit team member review vote!`, error);
      throw error;
    }
  }

  // Tattoos: Categories
  static async getTattooCategories(): Promise<ApiResponse<TattooCategory[]>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getTattooCategories);
      return wrapAsApiResponse<TattooCategory[]>(response.data);
    } catch (error) {
      console.error('Error fetching tattoo categories:', error);
      throw error;
    }
  }

  static async getTattooCategoryById(categoryId: number): Promise<ApiResponse<TattooCategory>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getTattooCategory(categoryId));
      return wrapAsApiResponse<TattooCategory>(response.data);
    } catch (error) {
      console.error('Error fetching tattoo category:', error);
      throw error;
    }
  }

  static async getTattooCategoryByName(name: string): Promise<ApiResponse<TattooCategory>> {
    try {
      const encoded = encodeURIComponent(name);
      const response = await apiClient.get(ApiEndpoints.getTattooCategoryByName(name));
      return wrapAsApiResponse<TattooCategory>(response.data);
    } catch (error) {
      console.error('Error fetching tattoo category by name:', error);
      throw error;
    }
  }

  static async createTattooCategory(request: CreateTattooCategoryRequest): Promise<ApiResponse<TattooCategory>> {
    try {
      const response = await apiClient.post(ApiEndpoints.createTattooCategory, request);
      return wrapAsApiResponse<TattooCategory>(response.data);
    } catch (error) {
      console.error('Error creating tattoo category:', error);
      throw error;
    }
  }

  static async updateTattooCategory(categoryId: number, request: UpdateTattooCategoryRequest): Promise<ApiResponse<TattooCategory>> {
    try {
      const response = await apiClient.put(ApiEndpoints.updateTattooCategory(categoryId), request);
      return wrapAsApiResponse<TattooCategory>(response.data);
    } catch (error) {
      console.error('Error updating tattoo category:', error);
      throw error;
    }
  }

  static async deleteTattooCategory(categoryId: number): Promise<ApiResponse<null>> {
    try {
      const response = await apiClient.delete(ApiEndpoints.deleteTattooCategory(categoryId));
      return wrapAsApiResponse<null>(response.data);
    } catch (error) {
      console.error('Error deleting tattoo category:', error);
      throw error;
    }
  }

  // Tattoos: Styles
  static async getTattooStyles(): Promise<ApiResponse<TattooStyle[]>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getTattooStyles);
      return wrapAsApiResponse<TattooStyle[]>(response.data);
    } catch (error) {
      console.error('Error fetching tattoo styles:', error);
      throw error;
    }
  }

  static async getTattooStylesByCategoryId(categoryId: number): Promise<ApiResponse<TattooStyle[]>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getTattooStylesByCategory(categoryId));
      return wrapAsApiResponse<TattooStyle[]>(response.data);
    } catch (error) {
      console.error('Error fetching tattoo styles by category:', error);
      throw error;
    }
  }

  static async getTattooStyleById(styleId: number): Promise<ApiResponse<TattooStyle>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getTattooStyle(styleId));
      return wrapAsApiResponse<TattooStyle>(response.data);
    } catch (error) {
      console.error('Error fetching tattoo style:', error);
      throw error;
    }
  }

  static async getTattooStyleByName(name: string): Promise<ApiResponse<TattooStyle>> {
    try {
      const encoded = encodeURIComponent(name);
      const response = await apiClient.get(ApiEndpoints.getTattooStyleByName(name));
      return wrapAsApiResponse<TattooStyle>(response.data);
    } catch (error) {
      console.error('Error fetching tattoo style by name:', error);
      throw error;
    }
  }

  static async createTattooStyle(request: CreateTattooStyleRequest): Promise<ApiResponse<TattooStyle>> {
    try {
      const response = await apiClient.post(ApiEndpoints.createTattooStyle, request);
      return wrapAsApiResponse<TattooStyle>(response.data);
    } catch (error) {
      console.error('Error creating tattoo style:', error);
      throw error;
    }
  }

  static async updateTattooStyle(styleId: number, request: UpdateTattooStyleRequest): Promise<ApiResponse<TattooStyle>> {
    try {
      const response = await apiClient.put(ApiEndpoints.updateTattooStyle(styleId), request);
      return wrapAsApiResponse<TattooStyle>(response.data);
    } catch (error) {
      console.error('Error updating tattoo style:', error);
      throw error;
    }
  }

  static async deleteTattooStyle(styleId: number): Promise<ApiResponse<null>> {
    try {
      const response = await apiClient.delete(ApiEndpoints.deleteTattooStyle(styleId));
      return wrapAsApiResponse<null>(response.data);
    } catch (error) {
      console.error('Error deleting tattoo style:', error);
      throw error;
    }
  }

  // Artist-Tattoo Styles
  static async getArtistTattooStyles(artistId: string): Promise<ApiResponse<ArtistTattooStyleAssignment[]>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getArtistTattooStyles(artistId));
      return wrapAsApiResponse<ArtistTattooStyleAssignment[]>(response.data);
    } catch (error) {
      console.error('Error fetching artist tattoo styles:', error);
      throw error;
    }
  }

  static async getArtistTattooStyleAssignmentById(
    artistId: string,
    artistStyleId: number
  ): Promise<ApiResponse<ArtistTattooStyleAssignment>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getArtistTattooStyleAssignment(artistId, artistStyleId));
      return wrapAsApiResponse<ArtistTattooStyleAssignment>(response.data);
    } catch (error) {
      console.error('Error fetching artist tattoo style assignment:', error);
      throw error;
    }
  }

  static async addTattooStyleToArtist(
    artistId: string,
    request: AddArtistTattooStyleRequest
  ): Promise<ApiResponse<ArtistTattooStyleAssignment>> {
    try {
      const response = await apiClient.post(ApiEndpoints.addTattooStyleToArtist(artistId), request);
      return wrapAsApiResponse<ArtistTattooStyleAssignment>(response.data);
    } catch (error) {
      console.error('Error adding tattoo style to artist:', error);
      throw error;
    }
  }

  static async updateArtistTattooStyleAssignment(
    artistId: string,
    artistStyleId: number,
    request: UpdateArtistTattooStyleAssignmentRequest
  ): Promise<ApiResponse<ArtistTattooStyleAssignment>> {
    try {
      const response = await apiClient.put(ApiEndpoints.updateArtistTattooStyleAssignment(artistId, artistStyleId), request);
      return wrapAsApiResponse<ArtistTattooStyleAssignment>(response.data);
    } catch (error) {
      console.error('Error updating artist tattoo style assignment:', error);
      throw error;
    }
  }

  static async removeArtistTattooStyleByAssignmentId(
    artistId: string,
    artistStyleId: number
  ): Promise<ApiResponse<null>> {
    try {
      const response = await apiClient.delete(ApiEndpoints.removeArtistTattooStyleByAssignmentId(artistId, artistStyleId));
      return wrapAsApiResponse<null>(response.data);
    } catch (error) {
      console.error('Error removing artist tattoo style by assignment id:', error);
      throw error;
    }
  }

  static async removeArtistTattooStyleByStyleId(
    artistId: string,
    styleId: number
  ): Promise<ApiResponse<null>> {
    try {
      const response = await apiClient.delete(ApiEndpoints.removeArtistTattooStyleByStyleId(artistId, styleId));
      return wrapAsApiResponse<null>(response.data);
    } catch (error) {
      console.error('Error removing artist tattoo style by style id:', error);
      throw error;
    }
  }

  // Artist Follows
  static async getIsFollowingArtist(artistId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getIsFollowingArtist(artistId), {
        params: { artistId: artistId },
      });
      return wrapAsApiResponse<boolean>(response.data);
    } catch (error) {
      console.error('Error fetching follow status:', error);
      throw error;
    }
  }

  static async getArtistsFollowing(artistId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get(ApiEndpoints.getArtistFollowing(artistId));

      if (response.data.success && response.data) {
        return response.data;
      } else {
        console.error('API Error:', response.data.errors || 'Unknown error occurred');
        throw new Error('Error finding artist followers: ' + artistId);
      }
    } catch (error) {
      console.error('Error fetching follow status:', error);
      throw error;
    }
  }

  static async followArtist(artistId: string): Promise<ApiResponse<any>> {
    try {
      console.log(artistId);
      const response = await apiClient.post(ApiEndpoints.followArtist(artistId), { artistUserID:artistId });
      return wrapAsApiResponse<any>(response.data);
    } catch (error) {
      console.error('Error following artist:', error);
      throw error;
    }
  }

  static async unfollowArtist(artistId: string): Promise<ApiResponse<any>> {
    try {
      // axios supports a request body for DELETE via config.data
      const response = await apiClient.delete(ApiEndpoints.unfollowArtist(artistId), {
        data: { artistId },
        params: { artistId },
      });
      return wrapAsApiResponse<any>(response.data);
    } catch (error) {
      console.error('Error unfollowing artist:', error);
      throw error;
    }
  }

}
